import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { VehiclesReservationsTableColumns } from 'src/features/reservations/components/tables/utils/vehiclesReservationsTableColumns';
import { useReservationsVehicleStayList } from 'src/features/reservations/hooks/useReservationsVehicleStayList';
import { RESERVATION_STATUS, RESERVATION_TYPE } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import useRowSelection from 'src/registry/hooks/useRowSelection';

const VehiclesReservationsTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'license_plate' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { ...rowSelection } = useRowSelection();
    const [searchParams] = useSearchParams();
    const query = Object.fromEntries(searchParams.entries());
    const { status, reservation_type, vehicle_stay_reservation_type } = query;

    const {
        data: vehiclesReservationData,
        refetch,
        isError,
        isLoading,
    } = useReservationsVehicleStayList({
        parkingId: parkingId,
        reservationType: reservation_type as RESERVATION_TYPE,
        status: status as RESERVATION_STATUS,
        vehicle_stay_reservation_type: vehicle_stay_reservation_type,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const columns = VehiclesReservationsTableColumns();

    return (
        <DataTable
            columns={columns}
            data={vehiclesReservationData}
            refetch={refetch}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            rowSelection={rowSelection}
            getRowId={(row) => row.id.toString()}
            semantics={{
                title: formatMessage(reservationMessages.reservations),
                entryName: formatMessage(reservationMessages.reservation),
                entryNames: formatMessage(reservationMessages.reservations),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            dataTableBodyProps={{
                onRowClickCallback: (row) => {
                    void navigate(`${row.id}`);
                },
            }}
            enableLocalFilters={true}
        />
    );
};

export default VehiclesReservationsTable;
